import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type StaffAddress, type StaffTaxForms, type TaxFormView, type W9Classification, type W9Input, W9_CLASSIFICATIONS, W9_CLASSIFICATION_LABELS } from "@vaulted/shared";
import { useEffect, useState } from "react";
import { errorMessage, get, post, put } from "../api";
import { useSession } from "../session";
import { Alert, Badge, Button, Checkbox, Dialog, Field, Panel, PanelHeader, SelectField, formatDateTime } from "./ui";

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

function Summary({ f }: { f: TaxFormView }) {
  return (
    <div className="text-sm">
      <div className="font-semibold">
        {f.name}
        {f.businessName ? ` (${f.businessName})` : ""}
      </div>
      <div className="text-ink-soft">
        {f.classification ? `${W9_CLASSIFICATION_LABELS[f.classification]} · ` : ""}
        {f.tinType === "ein" ? "EIN" : "SSN"} {f.tinMasked ?? "—"} · {f.kind === "signed" ? `signed ${formatDateTime(f.signedAt)}` : `uploaded ${fmtDate(f.signedAt)}${f.createdByName ? ` by ${f.createdByName}` : ""}`}
      </div>
    </div>
  );
}

/** Opens the PDF in a new tab (a short-lived link; the opening is logged). */
function OpenPdf({ path }: { path: string }) {
  const open = useMutation({
    mutationFn: async () => {
      // Opened first, then pointed at the link, so a pop-up blocker doesn't stop it.
      const w = window.open("", "_blank");
      const { url } = await get<{ url: string }>(path);
      if (w) w.location.href = url;
      else window.location.href = url;
    },
  });
  return (
    <>
      <Button variant="quiet" busy={open.isPending} onClick={() => open.mutate()}>
        Open PDF
      </Button>
      {open.error != null && <span className="text-sm text-danger">{errorMessage(open.error)}</span>}
    </>
  );
}

/** Your account → Tax form (W-9): fill it in and sign it, or open the one on file. */
export function MyW9() {
  const q = useQuery({ queryKey: ["my-w9"], queryFn: () => get<StaffTaxForms & { certification: string; address: StaffAddress | null }>("/api/me/w9") });
  const [filling, setFilling] = useState(false);
  const [done, setDone] = useState(false);
  const loaded = !!q.data;
  // Opened from the text or Today (…/account#w9): scroll to it once it's on the page.
  useEffect(() => {
    if (loaded && window.location.hash === "#w9") document.getElementById("w9")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [loaded]);
  if (!q.data) return null;
  const cur = q.data.current;
  return (
    <Panel>
      <div id="w9" />
      <PanelHeader
        title="Tax form (W-9)"
        description="The clinic needs a W-9 to pay you as a contractor. Fill it in and sign it here; your Social Security or EIN number is kept encrypted, and only you and the people who run payroll can open it."
        action={
          <Button variant={cur ? "quiet" : "primary"} onClick={() => setFilling(true)}>
            {cur ? "Fill out a new one" : "Fill out your W-9"}
          </Button>
        }
      />
      <div className="flex flex-col gap-3 px-5 py-5">
        {done && <Alert tone="ok">Signed. Thank you!</Alert>}
        {q.data.requestedAt && !cur && <Alert tone="info">The clinic asked you to fill this out.</Alert>}
        {cur ? (
          <div className="flex flex-wrap items-center gap-3">
            <Summary f={cur} />
            <OpenPdf path={`/api/me/w9/${cur.id}/pdf`} />
          </div>
        ) : (
          <p className="text-sm text-muted">Nothing on file yet.</p>
        )}
      </div>
      {filling && (
        <W9Dialog
          certification={q.data.certification}
          address={q.data.address}
          onClose={() => setFilling(false)}
          onDone={() => {
            setFilling(false);
            setDone(true);
          }}
        />
      )}
    </Panel>
  );
}

function W9Dialog({ certification, address, onClose, onDone }: { certification: string; address: StaffAddress | null; onClose: () => void; onDone: () => void }) {
  const qc = useQueryClient();
  const { me } = useSession();
  const [v, setV] = useState<W9Input>({
    name: me ? `${me.staff.firstName} ${me.staff.lastName}` : "",
    businessName: "",
    classification: "individual",
    llcClass: "",
    otherText: "",
    foreignPartners: false,
    exemptPayeeCode: "",
    fatcaCode: "",
    // Pre-filled from their address on file; what they sign becomes the address on file.
    address1: address?.line1 ?? "",
    address2: address?.line2 ?? "",
    city: address?.city ?? "",
    state: address?.state ?? "",
    postalCode: address?.postalCode ?? "",
    tinType: "ssn",
    tin: "",
    crossOutItem2: false,
    agree: false as unknown as true,
  });
  const set = <K extends keyof W9Input>(k: K, value: W9Input[K]) => setV((x) => ({ ...x, [k]: value }));
  const [more, setMore] = useState(false);
  const sign = useMutation({
    mutationFn: () => post<StaffTaxForms>("/api/me/w9", v),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["my-w9"] });
      await qc.invalidateQueries({ queryKey: ["me"] });
      onDone();
    },
  });
  const digits = (v.tin ?? "").replace(/\D/g, "");
  const ready = !!v.name && !!v.address1 && !!v.city && !!v.state && !!v.postalCode && digits.length === 9 && v.agree === true;
  return (
    <Dialog
      title="Form W-9"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={sign.isPending} disabled={!ready} onClick={() => sign.mutate()}>
            Sign
          </Button>
        </>
      }
    >
      <form className="flex flex-col gap-4" autoComplete="off" onSubmit={(e) => e.preventDefault()}>
        {sign.error != null && <Alert>{errorMessage(sign.error)}</Alert>}
        <p className="text-sm text-ink-soft">This fills in the IRS Form W-9 (Request for Taxpayer Identification Number and Certification) and signs it with your electronic signature. You get a copy as a PDF.</p>
        <Field label="1. Name (as shown on your income tax return)" required maxLength={120} value={v.name} onChange={(e) => set("name", e.target.value)} />
        <Field label="2. Business name, if different (optional)" maxLength={120} value={v.businessName ?? ""} onChange={(e) => set("businessName", e.target.value)} />
        <SelectField label="3a. Federal tax classification" value={v.classification ?? "individual"} onChange={(e) => set("classification", e.target.value as W9Classification)} hint="Most people: Individual / sole proprietor.">
          {W9_CLASSIFICATIONS.map((c) => (
            <option key={c} value={c}>
              {W9_CLASSIFICATION_LABELS[c]}
            </option>
          ))}
        </SelectField>
        {v.classification === "llc" && (
          <SelectField label="LLC taxed as" value={v.llcClass ?? ""} onChange={(e) => set("llcClass", e.target.value as W9Input["llcClass"])}>
            <option value="">Pick one…</option>
            <option value="C">C corporation</option>
            <option value="S">S corporation</option>
            <option value="P">Partnership</option>
          </SelectField>
        )}
        {v.classification === "other" && <Field label="Other (say what kind)" maxLength={80} value={v.otherText ?? ""} onChange={(e) => set("otherText", e.target.value)} />}
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_140px]">
          <Field label="5. Street address" required maxLength={120} autoComplete="address-line1" value={v.address1} onChange={(e) => set("address1", e.target.value)} />
          <Field label="Apt or suite" maxLength={60} autoComplete="address-line2" value={v.address2 ?? ""} onChange={(e) => set("address2", e.target.value)} />
        </div>
        <div className="grid grid-cols-[minmax(0,1fr)_90px_110px] gap-3">
          <Field label="6. City" required maxLength={60} autoComplete="address-level2" value={v.city} onChange={(e) => set("city", e.target.value)} />
          <Field label="State" required maxLength={30} autoComplete="address-level1" value={v.state} onChange={(e) => set("state", e.target.value)} />
          <Field label="ZIP" required maxLength={12} autoComplete="postal-code" inputMode="numeric" value={v.postalCode} onChange={(e) => set("postalCode", e.target.value)} />
        </div>
        {address && <p className="-mt-2 text-sm text-muted">From your address on file. If you change it here, it's updated there too.</p>}
        <div className="grid grid-cols-[auto_minmax(0,1fr)] items-end gap-3">
          <SelectField label="Part I. Taxpayer ID" value={v.tinType ?? "ssn"} onChange={(e) => set("tinType", e.target.value as "ssn" | "ein")}>
            <option value="ssn">Social Security number</option>
            <option value="ein">Employer ID number (EIN)</option>
          </SelectField>
          <Field
            label={v.tinType === "ein" ? "EIN" : "SSN"}
            required
            inputMode="numeric"
            autoComplete="off"
            placeholder={v.tinType === "ein" ? "12-3456789" : "123-45-6789"}
            maxLength={11}
            value={v.tin}
            onChange={(e) => set("tin", e.target.value.replace(/[^\d-]/g, ""))}
          />
        </div>
        <button type="button" className="self-start text-sm font-semibold text-brand underline" onClick={() => setMore((x) => !x)}>
          {more ? "Hide" : "Show"} the rarely used parts (exemption codes, foreign partners)
        </button>
        {more && (
          <div className="flex flex-col gap-3 rounded-xl border border-line p-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="4. Exempt payee code (if any)" maxLength={4} value={v.exemptPayeeCode ?? ""} onChange={(e) => set("exemptPayeeCode", e.target.value)} />
              <Field label="FATCA exemption code (if any)" maxLength={4} value={v.fatcaCode ?? ""} onChange={(e) => set("fatcaCode", e.target.value)} />
            </div>
            {(v.classification === "partnership" || v.classification === "trust" || (v.classification === "llc" && v.llcClass === "P")) && (
              <Checkbox label="3b. This entity has foreign partners, owners, or beneficiaries" checked={!!v.foreignPartners} onChange={(x) => set("foreignPartners", x)} />
            )}
            <Checkbox
              label="Cross out item 2"
              description="Only if the IRS has told you that you are currently subject to backup withholding because you failed to report all interest and dividends."
              checked={!!v.crossOutItem2}
              onChange={(x) => set("crossOutItem2", x)}
            />
          </div>
        )}
        {/* The font draws "(c)" as ©; a zero-width break keeps it as written. */}
        <div className="rounded-xl bg-mist p-3 text-sm whitespace-pre-line text-ink-soft">{certification.replace("(c)", "(c\u200C)")}</div>
        <Checkbox label="I certify the above, and sign this form electronically" checked={v.agree === true} onChange={(x) => set("agree", x as true)} />
      </form>
    </Dialog>
  );
}

/** Staff → name → Tax form (W-9), for people with "Manage payouts". */
export function StaffW9Panel({ staffId, name }: { staffId: string; name: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["staff-w9", staffId], queryFn: () => get<StaffTaxForms>(`/api/staff/${staffId}/w9`) });
  const setData = (d: StaffTaxForms) => qc.setQueryData(["staff-w9", staffId], d);
  const { me } = useSession();
  const [asked, setAsked] = useState<string | null>(null);
  const ask = useMutation({
    mutationFn: () => post<StaffTaxForms & { textProblem: string | null }>(`/api/staff/${staffId}/w9/request`),
    onSuccess: async (d) => {
      setData(d);
      setAsked(d.textProblem ?? `Texted ${name} a link to the form.`);
      // Asking yourself: your own Today reminder shows straight away.
      if (staffId === me?.staff.id) await qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
  const [uploading, setUploading] = useState(false);
  if (!q.data) return null;
  const cur = q.data.current;
  return (
    <Panel className="mt-6">
      <PanelHeader
        title="Tax form (W-9)"
        description={`Asking texts ${name} a link and puts a reminder on their Today; they fill it in and sign it under Your account → Tax form. Their taxpayer ID is encrypted; only the last 4 show here, and every opening of the PDF is logged.`}
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="quiet" onClick={() => setUploading(true)}>
              Upload a W-9
            </Button>
            <Button variant={cur || q.data.requestedAt ? "quiet" : "primary"} busy={ask.isPending} onClick={() => ask.mutate()}>
              {q.data.requestedAt ? "Text them again" : cur ? "Ask for a new one" : "Ask them to fill it out"}
            </Button>
          </div>
        }
      />
      <div className="flex flex-col gap-3 px-5 py-5">
        {ask.error != null && <Alert>{errorMessage(ask.error)}</Alert>}
        {asked && <Alert tone="info">{asked}</Alert>}
        <div className="flex flex-wrap items-center gap-3">
          {cur ? <Badge tone="ok">On file</Badge> : q.data.requestedAt ? <Badge tone="wait">Requested</Badge> : <Badge tone="off">Not on file</Badge>}
          {q.data.requestedAt && <span className="text-sm text-muted">Asked {fmtDate(q.data.requestedAt)}; they see a reminder on Today until it's done.</span>}
        </div>
        {cur && (
          <div className="flex flex-wrap items-center gap-3">
            <Summary f={cur} />
            <OpenPdf path={`/api/staff/${staffId}/w9/${cur.id}/pdf`} />
          </div>
        )}
        {q.data.history.length > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer text-muted">Earlier W-9s ({q.data.history.length})</summary>
            <ul className="mt-2 flex flex-col gap-2">
              {q.data.history.map((f) => (
                <li key={f.id} className="flex flex-wrap items-center gap-3">
                  <Summary f={f} />
                  <OpenPdf path={`/api/staff/${staffId}/w9/${f.id}/pdf`} />
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
      {uploading && <UploadW9 staffId={staffId} name={name} onClose={() => setUploading(false)} onDone={(d) => (setData(d), setUploading(false))} />}
    </Panel>
  );
}

function UploadW9({ staffId, name, onClose, onDone }: { staffId: string; name: string; onClose: () => void; onDone: (d: StaffTaxForms) => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [legal, setLegal] = useState(name);
  const [tinType, setTinType] = useState<"ssn" | "ein">("ssn");
  const [last4, setLast4] = useState("");
  const save = useMutation({
    mutationFn: async () => {
      const up = await post<{ key: string; url: string; headers: Record<string, string> }>(`/api/staff/${staffId}/w9/upload-url`, { contentType: file!.type });
      const res = await fetch(up.url, { method: "PUT", headers: up.headers, body: file });
      if (!res.ok) throw new Error("The upload didn't go through. Try again.");
      return post<StaffTaxForms>(`/api/staff/${staffId}/w9/upload`, { key: up.key, name: legal, tinType, tinLast4: last4 });
    },
    onSuccess: onDone,
  });
  return (
    <Dialog
      title="Upload a signed W-9"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} disabled={!file || !legal.trim() || !/^\d{4}$/.test(last4)} onClick={() => save.mutate()}>
            Upload
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {save.error != null && <Alert>{errorMessage(save.error)}</Alert>}
        <label className="flex flex-col gap-1 text-sm font-semibold">
          The signed form (PDF, JPG, or PNG)
          <input type="file" accept="application/pdf,image/jpeg,image/png" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </label>
        <Field label="Name on the form" value={legal} maxLength={120} onChange={(e) => setLegal(e.target.value)} />
        <div className="grid grid-cols-2 gap-3">
          <SelectField label="Taxpayer ID" value={tinType} onChange={(e) => setTinType(e.target.value as "ssn" | "ein")}>
            <option value="ssn">SSN</option>
            <option value="ein">EIN</option>
          </SelectField>
          <Field label="Last 4 digits" inputMode="numeric" maxLength={4} value={last4} onChange={(e) => setLast4(e.target.value.replace(/\D/g, ""))} hint="So you can tell forms apart; the full number stays on the form only." />
        </div>
      </div>
    </Dialog>
  );
}

/** Street, apt, city, state, ZIP; all blank = no address (null). */
export function AddressFields({ value, onChange }: { value: StaffAddress | null; onChange: (a: StaffAddress | null) => void }) {
  const a = value ?? { line1: "", line2: "", city: "", state: "", postalCode: "" };
  const set = (k: keyof StaffAddress, x: string) => {
    const next = { ...a, [k]: x };
    onChange(Object.values(next).every((y) => !y.trim()) ? null : next);
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_140px]">
        <Field label="Street address" maxLength={120} autoComplete="address-line1" value={a.line1} onChange={(e) => set("line1", e.target.value)} />
        <Field label="Apt or suite" maxLength={60} autoComplete="address-line2" value={a.line2} onChange={(e) => set("line2", e.target.value)} />
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)_90px_110px] gap-3">
        <Field label="City" maxLength={60} autoComplete="address-level2" value={a.city} onChange={(e) => set("city", e.target.value)} />
        <Field label="State" maxLength={30} autoComplete="address-level1" value={a.state} onChange={(e) => set("state", e.target.value)} />
        <Field label="ZIP" maxLength={12} autoComplete="postal-code" inputMode="numeric" value={a.postalCode} onChange={(e) => set("postalCode", e.target.value)} />
      </div>
    </div>
  );
}

/** Your account → Your address (also on your W-9 and contract). */
export function MyAddress() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["my-address"], queryFn: () => get<{ address: StaffAddress | null }>("/api/me/address") });
  const [draft, setDraft] = useState<StaffAddress | null | undefined>(undefined);
  const save = useMutation({
    mutationFn: () => put<{ address: StaffAddress | null }>("/api/me/address", { address: draft ?? null }),
    onSuccess: (d) => (qc.setQueryData(["my-address"], d), qc.invalidateQueries({ queryKey: ["my-w9"] }), setDraft(undefined)),
  });
  if (!q.data) return null;
  const value = draft === undefined ? q.data.address : draft;
  return (
    <Panel>
      <PanelHeader title="Your address" description="Your mailing address. It fills in your W-9 and your contract, and signing a W-9 updates it." />
      <form
        className="flex flex-col gap-3 px-5 py-5"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {save.error != null && <Alert>{errorMessage(save.error)}</Alert>}
        <AddressFields value={value} onChange={setDraft} />
        <div className="flex items-center gap-3">
          <Button type="submit" variant="primary" busy={save.isPending} disabled={draft === undefined}>
            Save
          </Button>
          {save.isSuccess && draft === undefined && <span className="text-sm text-ok">Saved.</span>}
        </div>
      </form>
    </Panel>
  );
}
